import React, { useState, useEffect, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Typography, Box } from '@mui/material';
import { FONT_COLOR } from '../const/general'
import Progress from '../item/progress'

export default function ShelterAnalysisLine(props) {
    const [lineData, setLineData] = useState([]);
    const [categoryDates, setCategoryDates] = useState([]);
    const [seriesMax, setSeriesMax] = useState(0);
    const [isNoData, setIsNoData] = useState(false);

    // チャートごとの一意のキー
    const chartId = useId();

    const aryMax = function (a, b) { return Math.max(a, b); }

    // チャート情報を格納
    const setChartData = (date_ary, chartData) => {
        setCategoryDates(date_ary);

        if (series.length > 0) {
            // 河川水位、最大値を取得
            setSeriesMax(props.limitVal + 1)
            if (chartData.length > 0 && chartData.reduce(aryMax) > props.limitVal) {
                setSeriesMax(series.reduce(aryMax))
            }
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    useEffect(() => {
        if (props.dateArray === null ||
            props.chartData === null ||
            (Array.isArray(props.chartData) && props.chartData.length === 0)) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.chartData && props.chartData.length > 0) {
            setChartData(props.dateArray, props.chartData)
        }
        setIsNoData(false)
        loadingEnd()
    }, [props.dateArray, props.chartData]);

    useEffect(() => {
        if (props.dateArray === null ||
            props.chartData === null ||
            (Array.isArray(props.chartData) && props.chartData.length === 0)) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.chartData && props.chartData.length > 0) {
            setChartData(props.dateArray, props.chartData)
        }
        setIsNoData(false)
        loadingEnd()

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    const series = useMemo(() => {
        return [
            {
                name: props.dataName,
                data: Array.isArray(props.chartData) ? props.chartData : []
            }
        ];
    }, [props.dataName, props.chartData]);

    const latestVal = useMemo(() => {
        const arr = props.chartData || [];
        if (!Array.isArray(arr) || arr.length === 0) return 0;
        const v = arr[arr.length - 1];
        return Number(v) || 0;
    }, [props.chartData]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            toolbar: {
                show: false,
                offsetY: 0
            },
            type: "line",
            stacked: false,
            zoom: {
                enabled: false
            }
        },
        // ポイントの数値表示
        dataLabels: {
            enabled: true,
            enabledOnSeries: [0],
            offsetY: -5,
            style: {
                fontSize: '12px',
                colors: ['white'],
                background: 'transparent'
            },
            background: {
                enabled: false
            }
        },
        stroke: {
            width: [2, 0],
            colors: ['#5c9fed',
                'transparent']
        },
        markers: {
            size: 4,                   // ← ここを 0 以外にする
            colors: ['#5c9fed'],       // マーカー塗り色
            strokeColors: '#a9a9a9',   // 外枠色
            strokeWidth: 1,
            hover: {
                size: 6,
                sizeOffset: 0
            }
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    position: 'top', // top, center, bottom
                }
            }
        },
        xaxis: {
            categories: categoryDates,
            labels: {
                rotate: 0,
                offsetX: 8,
                rotateAlways: false,
                hideOverlappingLabels: true,
                tickPlacement: 'on',
                align: 'center',
                offsetX: 0,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '10px',
                }
            }
        },
        yaxis: [
            {
                opposite: true,
                min: 0,
                forceNiceScale: true,
                seriesName: props.dataName,
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false,
                },
                labels: {
                    show: false,
                },
                lines: {
                    show: false
                }
            }
        ],
        tooltip: {
            x: {
                show: false
            },
            y: {
                formatter: function (val) {
                    return parseFloat(val).toFixed(0); // ← 小数なしを明示的に指定
                }
            },
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const val = series[seriesIndex][dataPointIndex];
                const name = w.config.series[seriesIndex].name;
                return `
                    <div style="padding:5px;">
                    ${props.dataName}: ${val}
                    </div>
                `;
            }
        },
        grid: {
            show: false, // 全体の罫線を消す
            padding: {
                left: 40,
                right: 12,
                top: -10,
                bottom: 0
            }
        },
        legend: {
            show: false,
            height: 0
        }
    }), [categoryDates, props.dataName]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
    }, [series]);

    return (
        <>
            {!props.isReadyFlg ?
                <Progress />
                :
                isNoData ?
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'white',
                        }}
                    >
                        対象データがありません
                    </Typography>
                    :
                    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <Box sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'white' }}>
                            <Box sx={{ position: 'relative', display: 'inline-block', lineHeight: 1 }}>
                                <Box component="span" sx={{ fontSize: '3rem', lineHeight: 1, display: 'inline-block', fontWeight: 'bold' }}>{String(Math.round(latestVal))}</Box>
                                <Box component="span" sx={{ position: 'absolute', right: -20, bottom: 0, fontSize: '1rem' }}>{props.unit}</Box>
                            </Box>
                        </Box>

                        <Box sx={{ width: '100%', height: '100%', pl: '100px', boxSizing: 'border-box' }}>
                            <Chart
                                options={options}
                                series={series}
                                type="line"
                                height="100%"
                                width="100%"
                                style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                            />
                        </Box>
                    </Box>
            }
        </>
    );
}